import React, {useState, useRef, useEffect} from "react";
import "../styles/ProjectCard.css";

const shouldSkipVideo = () => {
    const connection = navigator.connection;
    return Boolean(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || "")));
};

const ProjectCard = ({ project, onClick, onTechClick }) => {
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [isVideoMounted, setIsVideoMounted] = useState(false);
    const [isVideoReady, setIsVideoReady] = useState(false);
    const videoRef = useRef(null);

    useEffect(() => {
        if (!project.video || shouldSkipVideo()) return;
        const node = videoRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVideoMounted(true);
                node.play().catch(() => {});
            } else {
                node.pause();
            }
        }, { rootMargin: "200px", threshold: 0.2 });

        observer.observe(node);
        return () => observer.disconnect();
    }, [project.video]);

    useEffect(() => {
        if (!isVideoMounted) return;
        const node = videoRef.current;
        if (!node) return;
        node.load();
        node.play().catch(() => {});
    }, [isVideoMounted]);

    return (
        <div className="project-card d-flex flex-column position-relative overflow-hidden interactive" onClick={onClick}>
            {!isImageLoaded && (
                <div className="skeleton-overlay position-absolute top-0 left-0 w-100 h-100" />
            )}
            <img className={`object-fit-cover ${isImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                 src={project.poster || project.image}
                 alt={project.name}
                 loading="lazy"
                 onLoad={() => setIsImageLoaded(true)}
                 onError={() => setIsImageLoaded(true)} />
            {project.video && (
                <video ref={videoRef}
                       className={`project-video object-fit-cover position-absolute top-0 start-0 ${isVideoReady ? 'opacity-100' : 'opacity-0'}`}
                       poster={project.poster || project.image}
                       muted
                       loop
                       playsInline
                       preload="none"
                       aria-hidden="true"
                       tabIndex={-1}
                       onCanPlay={() => setIsVideoReady(true)}>
                    {isVideoMounted && <source src={`${project.video}.webm`} type="video/webm" />}
                    {isVideoMounted && <source src={`${project.video}.mp4`} type="video/mp4" />}
                </video>
            )}
            <div className="overlay position-absolute top-0 left-0 right-0 bottom-0 d-flex flex-column justify-content-end align-items-start">
                <h3>{project.name}</h3>
                <p>{project.shortDescription}</p>
                <div className="tech-list">
                    {project.technologies.map((tech, index) => (
                        <span key={index} onClick={(e) => {
                            if (onTechClick) {
                                e.stopPropagation();
                                onTechClick(tech);
                            }
                        }} style={{ cursor: "pointer" }}>{tech}</span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ProjectCard;
